import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import type { PaginatedListResponse } from 'components/v2/DataTable/DataTable';
import { studioFontEndpoint } from 'infrastructure/api/endpoints';
import useApi from 'infrastructure/api/useApi';
import fontsKeys from '../data/fontKeys';
import type { Font } from '../types';

const FONT_PAGE_SIZE = 1000;

interface UseGetUploadedFontsOptions {
  enabled?: boolean;
  skipAuthRedirect?: boolean;
}

const getPageParams = (skipCount: number, maxResultCount = FONT_PAGE_SIZE) => ({
  skipCount,
  maxResultCount,
});

const useGetUploadedFonts = ({
  enabled = true,
  skipAuthRedirect = false,
}: UseGetUploadedFontsOptions = {}) => {
  const { requestApi } = useApi();

  return useQuery<Font[]>({
    queryKey: fontsKeys.uploaded(),
    queryFn: async () => {
      const getFontsPage = (
        skipCount: number,
        maxResultCount = FONT_PAGE_SIZE
      ) =>
        requestApi(studioFontEndpoint, {
          params: getPageParams(skipCount, maxResultCount),
          skipAuthRedirect,
        }).then(
          (response: AxiosResponse<PaginatedListResponse<Font>>) =>
            response.data
        );

      const firstPage = await getFontsPage(0);
      const { items: firstPageItems = [], totalCount } = firstPage;

      if (!totalCount || firstPageItems.length >= totalCount) {
        return firstPageItems;
      }

      const effectivePageSize = firstPageItems.length || FONT_PAGE_SIZE;
      const remainingSkipCounts: number[] = [];

      for (
        let skipCount = effectivePageSize;
        skipCount < totalCount;
        skipCount += effectivePageSize
      ) {
        remainingSkipCounts.push(skipCount);
      }

      const remainingPages = await Promise.all(
        remainingSkipCounts.map(skipCount =>
          getFontsPage(skipCount, effectivePageSize)
        )
      );

      return remainingPages.reduce(
        (fonts, page) => [...fonts, ...(page.items || [])],
        firstPageItems
      );
    },
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

export default useGetUploadedFonts;
